import useMyFetch from "@stores/useMyFetch";

/**
 * API functions for chart templates
 */

export function createChartTemplatesApi(getBasePath, serverTemplates) {
  // surveyToken scopes the request to the report-rendering context (survey-gated).
  async function fetchTemplates(surveyToken = null) {
    const url = surveyToken ? `${getBasePath()}?survey_token=${surveyToken}` : getBasePath();
    const { response, data } = await useMyFetch(url).get().json();

    if (response.value.ok) {
      return { success: true, templates: data.value.chart_templates || data.value };
    } else {
      return { success: false, error: data.value?.error || "Failed to fetch chart templates" };
    }
  }

  async function fetchBaseTypes() {
    const { response, data } = await useMyFetch(`${getBasePath()}/base_types`).get().json();

    if (response.value.ok) {
      return { success: true, baseTypes: data.value.base_types };
    } else {
      return { success: false, error: data.value?.error || "Failed to fetch base types" };
    }
  }

  async function loadAllCharts(themeToken, pptxSourceId) {
    const { response, data } = await useMyFetch(`/master_reports/${themeToken}/pptx_imports/${pptxSourceId}`)
      .get()
      .json();

    if (response.value.ok) {
      return { success: true, charts: data.value.charts || [] };
    } else {
      return { success: false, error: data.value?.error || "Failed to load charts" };
    }
  }

  async function createTemplate(templateData, position = null) {
    const payload = {
      chart_template: {
        name: templateData.name,
        base_type: templateData.base_type,
        chart_type: templateData.chart_type,
        report_theme_palette: templateData.report_theme_palette,
        source_slide_index: templateData.source_slide_index,
        source_chart_index: templateData.source_chart_index,
        chart_xml: templateData.chart_xml,
        chart_rels: templateData.chart_rels || {},
        style_config: templateData.style_config || {},
        ...(position != null && { position }),
      },
    };

    const { response, data } = await useMyFetch(getBasePath()).post(payload).json();

    if (response.value.ok) {
      return { success: true, template: data.value.chart_template || data.value };
    } else {
      const errorMsg = data.value?.error || data.value?.errors?.join(", ") || "Failed to create template";
      return { success: false, error: errorMsg };
    }
  }

  async function updateTemplate(id, params) {
    const template = serverTemplates.value.find((t) => t.id === id);
    if (!template) {
      return { success: false, error: "Template not found" };
    }

    const { response, data } = await useMyFetch(`${getBasePath()}/${template.token}`)
      .patch({ chart_template: params })
      .json();

    if (response.value.ok) {
      return { success: true, template: data.value.chart_template || data.value };
    } else {
      return { success: false, error: data.value?.errors || "Failed to update template" };
    }
  }

  async function deleteTemplate(id) {
    const template = serverTemplates.value.find((t) => t.id === id);
    if (!template) {
      return { success: false, error: "Template not found" };
    }

    const { response, data } = await useMyFetch(`${getBasePath()}/${template.token}`).delete().json();

    if (response.value.ok || response.value.status === 204) {
      return { success: true };
    } else {
      const errorMsg = data.value?.error || data.value?.errors || "Failed to delete template";
      return { success: false, error: typeof errorMsg === "object" ? JSON.stringify(errorMsg) : errorMsg };
    }
  }

  return {
    fetchTemplates,
    fetchBaseTypes,
    loadAllCharts,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
}
